// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Orchestration: file intake, worker traffic, report state and export.
 *
 * Deliberately holds no mesh logic of its own — geometry lives in `mesh/`, the
 * GPU lives in `viewer.ts`, and this file only decides what happens when.
 */

import './styles/main.css';
import { Viewer } from './viewer';
import { emit, eventsAsText } from './eventlog';
import { MATERIALS, estimateMaterial, findMaterial } from './estimate';
import {
  type Check,
  closeModal,
  formatBytes,
  formatMeasure,
  formatNumber,
  initDrawer,
  initModals,
  renderChecks,
  renderKeyValues,
} from './ui';
import { initGlossary } from './glossary';
import type { Analysis, ExportTransform, MeshFormat, WorkerResponse } from './types';

const PREFS_KEY = 'meshprep.prefs.v1';

interface Prefs {
  material: string;
  infill: number;
  wireframe: boolean;
  problemEdges: boolean;
}

const el = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;

/* ─────────────────────────────── state ──────────────────────────────── */

let worker: Worker | null = null;
let viewer: Viewer | null = null;
let analysis: Analysis | null = null;
let filename = '';
let fileSize = 0;
let sourceFormat: MeshFormat = 'stl';
let requestId = 0;
let pendingAnalysisId = -1;
let pendingExportId = -1;

let unitMm = 1;
let userScale = 1;

const prefs: Prefs = loadPrefs();

/* ──────────────────────────────── boot ──────────────────────────────── */

function boot(): void {
  initModals();
  initGlossary();
  const drawer = initDrawer();

  emit('system', 'ok', 'Meshprep ready — everything runs locally in this tab');

  setupMaterials();
  setupIntake();
  setupViewport();
  setupControls();

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      if (closeModal()) return;
      drawer.close();
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target && /^(INPUT|SELECT|TEXTAREA)$/.test(target.tagName)) return;
    if (!analysis) return;

    const key = event.key.toLowerCase();
    if (key === 'w') toggleWireframe();
    else if (key === 'e') toggleProblemEdges();
    else if (key === 'r') {
      viewer?.resetView();
      emit('view', 'info', 'View reset');
    }
  });
}

/* ───────────────────────────── file intake ──────────────────────────── */

function setupIntake(): void {
  const dropzone = el<HTMLElement>('dropzone');
  const input = el<HTMLInputElement>('file-input');

  dropzone.addEventListener('click', () => input.click());
  dropzone.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      input.click();
    }
  });
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (file) void loadFile(file);
    input.value = '';
  });

  for (const type of ['dragenter', 'dragover'] as const) {
    dropzone.addEventListener(type, (event) => {
      event.preventDefault();
      dropzone.classList.add('over');
    });
  }
  for (const type of ['dragleave', 'dragend'] as const) {
    dropzone.addEventListener(type, () => dropzone.classList.remove('over'));
  }
  dropzone.addEventListener('drop', (event) => {
    event.preventDefault();
    dropzone.classList.remove('over');
    const file = event.dataTransfer?.files?.[0];
    if (file) void loadFile(file);
  });

  // Dropping anywhere on the page works too — hunting for the target is a
  // needless bit of precision to demand.
  window.addEventListener('dragover', (event) => event.preventDefault());
  window.addEventListener('drop', (event) => {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (file) void loadFile(file);
  });

  el<HTMLButtonElement>('sample-btn').addEventListener('click', () => void loadSample());

  el<HTMLButtonElement>('btn-change').addEventListener('click', () => input.click());
  el<HTMLButtonElement>('btn-retry').addEventListener('click', () => input.click());
}

async function loadSample(): Promise<void> {
  emit('file', 'info', 'Loading the bundled sample model');
  try {
    const res = await fetch('samples/demo.stl');
    if (!res.ok) throw new Error(`sample fetch ${res.status}`);
    const buffer = await res.arrayBuffer();
    // Route through the exact same path a dropped file takes.
    await loadFile(new File([buffer], 'demo.stl', { type: 'model/stl' }));
  } catch {
    showError(
      'Could not load the sample',
      'The bundled sample model failed to load. Check your connection and try again.',
    );
  }
}

async function loadFile(file: File): Promise<void> {
  emit('file', 'info', `Selected ${file.name} (${formatBytes(file.size)})`);
  try {
    const buffer = await file.arrayBuffer();
    startAnalysis(buffer, file.name, file.size);
  } catch {
    showError('That file could not be read', 'The browser refused to open it. Try choosing it again.');
  }
}

function ensureWorker(): Worker {
  if (worker) return worker;
  worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
  worker.onmessage = (event: MessageEvent<WorkerResponse>) => handleWorkerMessage(event.data);
  worker.onerror = () => {
    showError('The analysis engine stopped', 'Reload the page and try the file again.');
  };
  return worker;
}

function startAnalysis(buffer: ArrayBuffer, name: string, size: number): void {
  filename = name;
  fileSize = size;
  analysis = null;
  userScale = 1;

  el<HTMLElement>('intake').hidden = true;
  el<HTMLElement>('workspace').hidden = false;
  el<HTMLElement>('report').hidden = true;
  el<HTMLElement>('errorbox').hidden = true;
  el<HTMLElement>('progress').hidden = false;
  setVerdict('pending', 'Analysing…', 'Reading the file');
  el<HTMLElement>('pf-name').textContent = name;
  el<HTMLElement>('pf-meta').textContent = formatBytes(size);

  pendingAnalysisId = ++requestId;
  emit('parse', 'info', `Handing ${formatBytes(size)} to the worker`);
  ensureWorker().postMessage(
    { kind: 'analyse', id: pendingAnalysisId, buffer, filename: name },
    [buffer],
  );
}

/* ───────────────────────────── worker traffic ───────────────────────── */

function handleWorkerMessage(message: WorkerResponse): void {
  switch (message.kind) {
    case 'progress':
      if (message.id !== pendingAnalysisId && message.id !== pendingExportId) return;
      setProgress(message.stage, message.ratio);
      break;

    case 'analysed': {
      if (message.id !== pendingAnalysisId) return;
      analysis = message.analysis;
      sourceFormat = message.format;
      el<HTMLElement>('progress').hidden = true;

      if (message.declaredUnitMm > 0) {
        unitMm = message.declaredUnitMm;
        const select = el<HTMLSelectElement>('sel-unit');
        select.value = String(unitMm);
        emit('mesh', 'info', `File declares its units: ${unitMm} mm per unit`);
      }

      emit(
        'mesh',
        'ok',
        `${formatNumber(message.analysis.triangles)} triangles, ${formatNumber(message.analysis.vertices)} unique vertices`,
      );
      if (message.analysis.watertight) {
        emit('mesh', 'ok', 'Watertight — every edge is shared by exactly two triangles');
      } else {
        emit(
          'mesh',
          'warn',
          `${formatNumber(message.analysis.boundaryEdges)} open and ${formatNumber(message.analysis.nonManifoldEdges)} non-manifold edges found`,
        );
      }

      el<HTMLElement>('report').hidden = false;
      renderReport();

      if (viewer) {
        viewer.setMesh(message.positions, message.analysis.problemLines);
        applyViewerOptions();
        emit('view', 'ok', 'Uploaded to the GPU');
        const wireBtn = el<HTMLButtonElement>('btn-wire');
        wireBtn.disabled = !viewer.wireframeAvailable;
        wireBtn.title = viewer.wireframeAvailable
          ? 'Wireframe (W)'
          : 'Too many triangles to draw a useful wireframe';
      }
      break;
    }

    case 'exported': {
      if (message.id !== pendingExportId) return;
      pendingExportId = -1;
      el<HTMLElement>('progress').hidden = true;
      void deliver(message.buffer, message.mime, message.extension);
      break;
    }

    case 'error':
      el<HTMLElement>('progress').hidden = true;
      if (message.id === pendingExportId) {
        pendingExportId = -1;
        setActionMessage(message.message, true);
        emit('export', 'err', message.message);
      } else {
        showError('Could not read that file', message.message);
        emit('parse', 'err', message.message);
      }
      break;
  }
}

function setProgress(stage: string, ratio: number): void {
  el<HTMLElement>('progress').hidden = false;
  el<HTMLElement>('progress-fill').style.width = `${Math.round(ratio * 100)}%`;
  el<HTMLElement>('progress-label').textContent = `${stage}… ${Math.round(ratio * 100)}%`;
}

function showError(title: string, body: string): void {
  el<HTMLElement>('intake').hidden = true;
  el<HTMLElement>('workspace').hidden = false;
  el<HTMLElement>('progress').hidden = true;
  el<HTMLElement>('report').hidden = true;
  const box = el<HTMLElement>('errorbox');
  box.hidden = false;
  el<HTMLElement>('err-title').textContent = title;
  el<HTMLElement>('err-body').textContent = body;
  setVerdict('bad', 'Could not analyse', title);
}

/* ─────────────────────────────── report ─────────────────────────────── */

function scale(): number {
  return unitMm * userScale;
}

function renderReport(): void {
  if (!analysis) return;
  const s = scale();
  const size = analysis.bbox.size.map((v) => v * s) as [number, number, number];
  const volumeMm3 = analysis.volume * s * s * s;
  const areaMm2 = analysis.area * s * s;

  renderChecks(el<HTMLElement>('checks'), buildChecks(analysis));
  updateVerdict(analysis);

  renderKeyValues(el<HTMLElement>('kv-size'), [
    { label: 'Width (X)', value: `${formatMeasure(size[0])} mm` },
    { label: 'Depth (Y)', value: `${formatMeasure(size[1])} mm` },
    { label: 'Height (Z)', value: `${formatMeasure(size[2])} mm` },
    {
      label: 'Volume',
      value: analysis.watertight
        ? `${formatMeasure(volumeMm3 / 1000)} cm³`
        : `${formatMeasure(volumeMm3 / 1000)} cm³ (unreliable — mesh is open)`,
      term: 'volume',
    },
    { label: 'Surface area', value: `${formatMeasure(areaMm2 / 100)} cm²` },
  ]);

  renderKeyValues(el<HTMLElement>('kv-geo'), [
    { label: 'Triangles', value: formatNumber(analysis.triangles) },
    { label: 'Unique vertices', value: formatNumber(analysis.vertices), term: 'soup' },
    { label: 'Open edges', value: formatNumber(analysis.boundaryEdges), term: 'boundary-edge' },
    {
      label: 'Non-manifold edges',
      value: formatNumber(analysis.nonManifoldEdges),
      term: 'non-manifold',
    },
    { label: 'Shells', value: formatNumber(analysis.shells), term: 'shell' },
    { label: 'Degenerate faces', value: formatNumber(analysis.degenerate), term: 'degenerate' },
    { label: 'Duplicate faces', value: formatNumber(analysis.duplicateTriangles) },
    { label: 'Source format', value: sourceFormat === 'stla' ? 'STL (ASCII)' : sourceFormat.toUpperCase() },
  ]);

  renderEstimate(volumeMm3, areaMm2);
}

function renderEstimate(volumeMm3: number, areaMm2: number): void {
  const material = findMaterial(el<HTMLSelectElement>('sel-material').value);
  const infill = Number(el<HTMLInputElement>('in-infill').value) / 100;
  const estimate = estimateMaterial(volumeMm3, areaMm2, infill, material);

  const rows = [
    { label: 'Material used', value: `${formatMeasure(estimate.materialCm3)} cm³`, term: 'infill' },
    { label: 'Weight', value: `${formatMeasure(estimate.grams)} g` },
  ];
  if (estimate.metres !== null) {
    rows.push({ label: 'Filament (1.75 mm)', value: `${formatMeasure(estimate.metres)} m` });
  }
  rows.push({
    label: `Cost at $${material.pricePerKg}/kg`,
    value: `$${estimate.costUsd.toFixed(2)}`,
  });
  renderKeyValues(el<HTMLElement>('kv-cost'), rows);
}

function buildChecks(a: Analysis): Check[] {
  const checks: Check[] = [];

  checks.push({
    label: 'Watertight',
    term: 'watertight',
    state: a.boundaryEdges === 0 ? 'ok' : 'bad',
    detail:
      a.boundaryEdges === 0
        ? 'No holes — the surface fully encloses a volume.'
        : `${formatNumber(a.boundaryEdges)} open edges. The red lines on the model show where.`,
  });

  checks.push({
    label: 'Manifold',
    term: 'non-manifold',
    state: a.nonManifoldEdges === 0 ? 'ok' : 'bad',
    detail:
      a.nonManifoldEdges === 0
        ? 'No edge is shared by more than two triangles.'
        : `${formatNumber(a.nonManifoldEdges)} edges have three or more faces meeting at them.`,
  });

  checks.push({
    label: 'Consistent winding',
    state: a.flippedEdges === 0 ? 'ok' : 'warn',
    detail:
      a.flippedEdges === 0
        ? 'Neighbouring triangles agree on which side is outside.'
        : `${formatNumber(a.flippedEdges)} edges join faces wound in opposite directions.`,
  });

  checks.push({
    label: 'Normals point outward',
    term: 'normals',
    state: a.inverted ? 'warn' : 'ok',
    detail: a.inverted
      ? 'The enclosed volume is negative — this model is inside-out.'
      : a.watertight
        ? 'The surface is wound outward.'
        : 'Not conclusive while the mesh is open.',
  });

  checks.push({
    label: 'Single body',
    term: 'shell',
    state: a.shells <= 1 ? 'ok' : 'warn',
    detail:
      a.shells <= 1
        ? 'One connected shell.'
        : `${formatNumber(a.shells)} separate shells — intentional for a multi-part model, otherwise stray geometry.`,
  });

  if (a.degenerate > 0 || a.duplicateTriangles > 0) {
    checks.push({
      label: 'Clean faces',
      term: 'degenerate',
      state: 'warn',
      detail: `${formatNumber(a.degenerate)} zero-area and ${formatNumber(a.duplicateTriangles)} duplicate triangles.`,
    });
  }

  return checks;
}

function updateVerdict(a: Analysis): void {
  if (!a.watertight) {
    const parts: string[] = [];
    if (a.boundaryEdges > 0) parts.push(`${formatNumber(a.boundaryEdges)} open edges`);
    if (a.nonManifoldEdges > 0) parts.push(`${formatNumber(a.nonManifoldEdges)} non-manifold edges`);
    setVerdict(
      'bad',
      'Not ready to print',
      `${parts.join(' and ')}. Repair it before slicing.`,
    );
    return;
  }
  const warnings =
    a.flippedEdges > 0 || a.inverted || a.shells > 1 || a.degenerate > 0 || a.duplicateTriangles > 0;
  if (warnings) {
    setVerdict('warn', 'Solid, with caveats', 'Watertight, but see the flagged checks below.');
    return;
  }
  setVerdict('ok', 'Ready to print', 'Watertight, manifold and consistently wound.');
}

function setVerdict(state: 'ok' | 'warn' | 'bad' | 'pending', title: string, sub: string): void {
  const box = el<HTMLElement>('verdict');
  box.dataset.state = state;
  el<HTMLElement>('verdict-title').textContent = title;
  el<HTMLElement>('verdict-sub').textContent = sub;
  const mark = el<HTMLElement>('verdict-mark');
  const paths: Record<string, string> = {
    ok: 'M20 6 9 17l-5-5',
    warn: 'M12 8v5M12 17h.01',
    bad: 'M18 6 6 18M6 6l12 12',
    pending: 'M12 6v6l4 2',
  };
  mark.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="${paths[state]}" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}

/* ────────────────────────────── viewport ────────────────────────────── */

function setupViewport(): void {
  const canvas = el<HTMLCanvasElement>('gl');
  try {
    viewer = Viewer.create(canvas);
  } catch (error) {
    viewer = null;
    emit('view', 'err', error instanceof Error ? error.message : 'WebGL failed to start');
  }

  if (!viewer) {
    canvas.hidden = true;
    const fallback = el<HTMLElement>('viewport-fallback');
    fallback.hidden = false;
    fallback.textContent =
      'This browser has no WebGL2, so the 3D preview is unavailable. Every measurement and check on the right still works.';
    emit('view', 'warn', 'WebGL2 unavailable — running without the 3D preview');
    return;
  }

  // The canvas is sized by CSS; re-render whenever that size changes.
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(() => viewer?.requestRender()).observe(el<HTMLElement>('viewport'));
  }
}

function applyViewerOptions(): void {
  viewer?.setOptions({ wireframe: prefs.wireframe, showProblemEdges: prefs.problemEdges });
  el<HTMLButtonElement>('btn-wire').setAttribute('aria-pressed', String(prefs.wireframe));
  el<HTMLButtonElement>('btn-edges').setAttribute('aria-pressed', String(prefs.problemEdges));
}

function toggleWireframe(): void {
  prefs.wireframe = !prefs.wireframe;
  savePrefs();
  applyViewerOptions();
  emit('view', 'info', `Wireframe ${prefs.wireframe ? 'on' : 'off'}`);
}

function toggleProblemEdges(): void {
  prefs.problemEdges = !prefs.problemEdges;
  savePrefs();
  applyViewerOptions();
  emit('view', 'info', `Open-edge highlight ${prefs.problemEdges ? 'on' : 'off'}`);
}

/* ────────────────────────────── controls ────────────────────────────── */

function setupMaterials(): void {
  const select = el<HTMLSelectElement>('sel-material');
  for (const material of MATERIALS) {
    const option = document.createElement('option');
    option.value = material.id;
    option.textContent = material.name;
    select.appendChild(option);
  }
  select.value = prefs.material;
  if (!select.value) select.value = MATERIALS[0].id;
  el<HTMLInputElement>('in-infill').value = String(prefs.infill);
}

function setupControls(): void {
  el<HTMLButtonElement>('btn-wire').addEventListener('click', toggleWireframe);
  el<HTMLButtonElement>('btn-edges').addEventListener('click', toggleProblemEdges);
  el<HTMLButtonElement>('btn-reset').addEventListener('click', () => viewer?.resetView());

  el<HTMLSelectElement>('sel-unit').addEventListener('change', (event) => {
    unitMm = Number((event.target as HTMLSelectElement).value) || 1;
    emit('mesh', 'info', `Interpreting file units as ${unitMm} mm`);
    renderReport();
  });

  el<HTMLInputElement>('in-scale').addEventListener('input', (event) => {
    const value = Number((event.target as HTMLInputElement).value);
    userScale = Number.isFinite(value) && value > 0 ? value : 1;
    renderReport();
  });

  el<HTMLButtonElement>('btn-fit').addEventListener('click', () => {
    if (!analysis) return;
    const target = Number(el<HTMLInputElement>('in-fit').value);
    const longest = Math.max(...analysis.bbox.size) * unitMm;
    if (!Number.isFinite(target) || target <= 0 || longest <= 0) {
      setActionMessage('Enter a target size in millimetres first.', true);
      return;
    }
    userScale = target / longest;
    el<HTMLInputElement>('in-scale').value = String(Math.round(userScale * 10000) / 10000);
    emit('mesh', 'info', `Scaled so the longest side is ${target} mm`);
    renderReport();
    setActionMessage(`Scale set to ×${formatMeasure(userScale)}.`);
  });

  for (const id of ['sel-material', 'in-infill']) {
    el<HTMLElement>(id).addEventListener('input', () => {
      prefs.material = el<HTMLSelectElement>('sel-material').value;
      prefs.infill = Number(el<HTMLInputElement>('in-infill').value) || 0;
      savePrefs();
      renderReport();
    });
  }

  el<HTMLButtonElement>('btn-export').addEventListener('click', () => runExport(false));
  el<HTMLButtonElement>('btn-copy').addEventListener('click', () => void copyReport());

  const share = el<HTMLButtonElement>('btn-share');
  if (typeof navigator.canShare === 'function') share.hidden = false;
  share.addEventListener('click', () => runExport(true));
}

/* ─────────────────────────────── export ─────────────────────────────── */

let shareNext = false;

function runExport(share: boolean): void {
  if (!analysis) return;
  shareNext = share;
  const transform: ExportTransform = {
    scale: scale(),
    center: el<HTMLInputElement>('ck-center').checked,
    dropToPlate: el<HTMLInputElement>('ck-drop').checked,
    yUpToZUp: el<HTMLInputElement>('ck-yup').checked,
  };
  const format = el<HTMLSelectElement>('sel-format').value as MeshFormat;
  pendingExportId = ++requestId;
  setActionMessage('Encoding…');
  emit('export', 'info', `Encoding as ${format.toUpperCase()} at scale ×${formatMeasure(transform.scale)}`);
  ensureWorker().postMessage({ kind: 'export', id: pendingExportId, format, transform });
}

async function deliver(buffer: ArrayBuffer, mime: string, extension: string): Promise<void> {
  const base = filename.replace(/\.[^.]+$/, '') || 'model';
  const name = `${base}-meshprep.${extension}`;
  const blob = new Blob([buffer], { type: mime });
  emit('export', 'ok', `${name} ready (${formatBytes(blob.size)})`);

  if (shareNext) {
    shareNext = false;
    const file = new File([blob], name, { type: mime });
    if (navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: name });
        setActionMessage('Shared.');
        return;
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          setActionMessage('');
          return;
        }
      }
    }
    setActionMessage('Sharing is unavailable here — downloading instead.');
  }

  const picker = (
    window as unknown as {
      showSaveFilePicker?: (options: unknown) => Promise<{
        createWritable: () => Promise<{ write: (data: Blob) => Promise<void>; close: () => Promise<void> }>;
      }>;
    }
  ).showSaveFilePicker;

  if (picker) {
    try {
      const handle = await picker({
        suggestedName: name,
        types: [{ description: `${extension.toUpperCase()} model`, accept: { [mime]: [`.${extension}`] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      setActionMessage(`Saved ${name}.`);
      return;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        setActionMessage('');
        return;
      }
      // Any other failure (an unsupported directory, a permissions quirk)
      // falls through to the plain download, which always works.
    }
  }

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
  setActionMessage(`Downloaded ${name}.`);
}

async function copyReport(): Promise<void> {
  if (!analysis) return;
  const text = buildReportText(analysis);
  try {
    await navigator.clipboard.writeText(text);
    setActionMessage('Report copied to the clipboard.');
    emit('export', 'ok', 'Report copied');
  } catch {
    setActionMessage('The browser blocked clipboard access.', true);
  }
}

function buildReportText(a: Analysis): string {
  const s = scale();
  const size = a.bbox.size.map((v) => v * s);
  const volumeMm3 = a.volume * s * s * s;
  const material = findMaterial(el<HTMLSelectElement>('sel-material').value);
  const infill = Number(el<HTMLInputElement>('in-infill').value) / 100;
  const estimate = estimateMaterial(volumeMm3, a.area * s * s, infill, material);

  return [
    `Meshprep report — ${filename}`,
    `Source: ${sourceFormat.toUpperCase()}, ${formatBytes(fileSize)}`,
    '',
    `Verdict: ${el<HTMLElement>('verdict-title').textContent}`,
    '',
    `Size: ${formatMeasure(size[0])} × ${formatMeasure(size[1])} × ${formatMeasure(size[2])} mm`,
    `Volume: ${formatMeasure(volumeMm3 / 1000)} cm³`,
    `Surface area: ${formatMeasure((a.area * s * s) / 100)} cm²`,
    '',
    `Triangles: ${formatNumber(a.triangles)}`,
    `Unique vertices: ${formatNumber(a.vertices)}`,
    `Open edges: ${formatNumber(a.boundaryEdges)}`,
    `Non-manifold edges: ${formatNumber(a.nonManifoldEdges)}`,
    `Inconsistently wound edges: ${formatNumber(a.flippedEdges)}`,
    `Shells: ${formatNumber(a.shells)}`,
    `Degenerate faces: ${formatNumber(a.degenerate)}`,
    `Duplicate faces: ${formatNumber(a.duplicateTriangles)}`,
    '',
    `Estimate (${material.name}, ${Math.round(infill * 100)}% infill): ${formatMeasure(estimate.grams)} g, $${estimate.costUsd.toFixed(2)}`,
    '',
    'Event log',
    eventsAsText(),
  ].join('\n');
}

function setActionMessage(text: string, isError = false): void {
  const box = el<HTMLElement>('actionmsg');
  box.textContent = text;
  box.dataset.tone = isError ? 'error' : 'info';
}

/* ─────────────────────────────── prefs ──────────────────────────────── */

function loadPrefs(): Prefs {
  const fallback: Prefs = { material: 'pla', infill: 20, wireframe: false, problemEdges: true };
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<Prefs>;
    return {
      material: typeof parsed.material === 'string' ? parsed.material : fallback.material,
      infill: typeof parsed.infill === 'number' ? parsed.infill : fallback.infill,
      wireframe: parsed.wireframe === true,
      problemEdges: parsed.problemEdges !== false,
    };
  } catch {
    return fallback;
  }
}

function savePrefs(): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // Private browsing with storage disabled — preferences simply do not persist.
  }
}

boot();
